<script setup lang="ts">
  /**
   * Section header — 03-components/12c-section-header · OSCloud DS 1.1.101
   *
   * `group` is the box rank (1.1.101): the header names ONE group rather than a page region
   * containing several, so its title takes the group-title type and its bottom gap tightens.
   * The heading level still follows the page outline, not the modifier.
   */
  import type { Component } from 'vue'

  import BfCounter from './BfCounter.vue'
  import type { BfCounterTone } from './BfCounterTypes'

  withDefaults(
    defineProps<{
      title: string
      icon?: Component | null
      /** Step marker; shares the icon slot, so use one or the other, never both. */
      number?: number | string | null
      count?: number | string | null
      countTone?: BfCounterTone
      /**
       * The card's title is an h2. A page that already names the section with its own
       * heading passes 3 — or 4 when that heading is itself under one — so the visible
       * title sits below it in the outline instead of beside it.
       */
      level?: 2 | 3 | 4
      /**
       * Box rank: the header names one group and is a peer of the .form-section /
       * .card--details boxes beside it, so it reads the group-title type.
       */
      group?: boolean
      /** Set when something else on the page points at the title with aria-labelledby. */
      titleId?: string
    }>(),
    { icon: null, number: null, count: null, countTone: 'neutral', level: 2, group: false, titleId: undefined },
  )
</script>

<template>
  <div class="section-header" :class="{ 'section-header--group': group }">
    <component :is="`h${level}`" :id="titleId" class="section-header__title">
      <span v-if="icon || $slots.icon" class="section-header__icon" aria-hidden="true">
        <component :is="icon" v-if="icon" class="section-header__icon-symbol" :size="16" />
        <slot v-else name="icon" />
      </span>
      <span v-else-if="number !== null" class="section-header__number" aria-hidden="true">{{ number }}</span>
      {{ title }}
      <BfCounter v-if="count !== null" class="section-header__count" :tone="countTone">{{ count }}</BfCounter>
    </component>

    <div v-if="$slots.actions" class="section-header__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .section-header {
    --section-header-icon-size: 30px; /* the card's declared exception to --bf-sp-* */
    --section-header-icon-gap: 12px; /* the card's declared exception to --bf-sp-* */
    /* Block-local: the rhythm under the header. The box rank tightens it. */
    --bf-section-header-gap: var(--bf-sp-2);
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: var(--bf-sp-2);
    margin: 0 0 var(--bf-section-header-gap);
    padding-bottom: var(--bf-sp-1);

    /* Box rank — the header names one group rather than a page region, so its title
       takes the group-title type (the three values .form-section__title reads) and the
       gap under it closes to the group's 8px. Icon, number, count and actions are
       unchanged at this rank. */
    &--group {
      --bf-section-header-gap: var(--bf-sp-1);
    }

    &--group .section-header__title {
      font-size: var(--bf-fs-base);
      font-weight: var(--bf-fw-semibold);
      color: var(--bf-deep-blue);
    }

    &__title {
      display: flex;
      align-items: center;
      gap: var(--section-header-icon-gap);
      margin: 0;
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-md);
      font-weight: var(--bf-fw-bold);
      line-height: var(--bf-lh-tight);
      letter-spacing: var(--bf-tr-tight);
      color: var(--bf-fg-1);
    }

    &__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      width: var(--section-header-icon-size);
      height: var(--section-header-icon-size);
      background: var(--bf-white);
      border: 1px solid var(--bf-line);
      border-radius: var(--bf-r-md);
      color: var(--bf-orange-dark);
    }

    &__icon-symbol {
      width: 16px;
      height: 16px;
      flex: 0 0 auto;
    }

    /* An icon passed through the slot is compiled in the consumer's scope, so it
       cannot take __icon-symbol from here — :slotted() gives it the same size. */
    &__icon :slotted(svg) {
      width: 16px;
      height: 16px;
      flex: 0 0 auto;
    }

    /* Same slot as __icon, a numeral instead of a symbol. Pill rather than the
       icon's rounded square so a numeral reads as a step, not as an icon. */
    &__number {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      width: var(--section-header-icon-size);
      height: var(--section-header-icon-size);
      background: var(--bf-warning-soft);
      border: 1px solid var(--bf-line);
      border-radius: var(--bf-r-pill);
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-xs);
      font-weight: var(--bf-fw-bold);
      line-height: 1;
      color: var(--bf-orange-dark);
      font-variant-numeric: tabular-nums;
    }

    &__count {
      margin-left: calc(6px - var(--section-header-icon-gap));
      align-self: center;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: var(--bf-sp-2);
      flex-wrap: wrap;
    }

    /* Text action — for flows where the section's only action is "Edit" /
       "Change" and an outline button would compete with the page CTA. Same
       recipe as .data-table__action; written by the consumer, so :deep(). */
    :deep(.section-header__action) {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 0;
      background: none;
      border: 0;
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-xs_5);
      font-weight: var(--bf-fw-semibold);
      letter-spacing: var(--bf-tr-nudge);
      line-height: 1.2;
      color: var(--bf-orange-dark);
      text-decoration: none;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
        text-underline-offset: 3px;
      }

      &:focus-visible {
        outline: none;
        box-shadow:
          0 0 0 2px hsl(var(--dk-background)),
          0 0 0 4px hsl(var(--dk-ring));
        border-radius: var(--bf-r-sm);
      }

      svg {
        width: 12px;
        height: 12px;
        flex: 0 0 auto;
      }
    }
  }

  @media (max-width: 600px) {
    /* --bf-bp-sm */
    .section-header__actions {
      width: 100%;
    }

    .section-header__actions :deep(.button) {
      flex: 1;
      justify-content: center;
    }
  }
</style>
